import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { Checkbox, Table, DatePicker, Button, Collapse } from 'antd'

import { pageChanged } from '../../../../../data/redux/page_details/actions'
import { getAccuracyFilters, getIntentsTableData, updateModelState } from '../../../../../data/redux/model_accuracy_details/actions'

import { APP_PAGES } from '../../../../../data/config/constants'
import { uniqueId, log } from '../../../../../data/config/utils'
import { IMG_EMPTY_MSG, IMG_ERROR, IMG_EMPTY } from '../../../../../data/assets/img'

import ShowMessage from '../../../../../components/showmessage'

const { RangePicker } = DatePicker
const { Panel } = Collapse

const ModelAccuracy = ({
  actions,
  screen_width,
  source_filters,
  selected_source_filters,
  selected_date_range,
  correct_intents,
  table_columns,
  intents_load_error,
  count_data,
}) => {
  log('ModelAccuracy rendered')

  const { pageChanged, getAccuracyFilters, getIntentsTableData, updateModelState } = actions
  useEffect(() => {
    pageChanged(APP_PAGES.MODEL_ACCURACY, APP_PAGES.ANALYTICS)
  }, [pageChanged])

  useEffect(() => {
    if (source_filters.length === 0)
      getAccuracyFilters()
  }, [getAccuracyFilters, source_filters.length])

  const handleSourceChange = source => updateModelState({ selected_source_filters: source })

  const handleDateRangeChange = date_range => updateModelState({ selected_date_range: date_range })

  const handleGetIntentsData = () => {
    if (selected_source_filters.length !== 0) {
      const filters = selected_source_filters.reduce((a, b) => (a[b] = true, a), {})
      const payload = {
        startDate: selected_date_range[0],
        endDate: selected_date_range[1],
        ...filters
      }
      // log(payload)
      getIntentsTableData(payload)
    }
  }

  if (!intents_load_error)
    return (
      <div className="ori-lr-pad-10">
        <div className="ori-t-pad-15 ori-b-pad-5 ori-flex-row ori-flex-jsb ori-flex-ac ori-flex-wrap ori-b-border-default">
          <div className="ori-b-mrgn-10 ori-l-mrgn-5 ori-flex-row ori-flex-jsb ori-flex-wrap ">
            <div className="ori-font-sm">
              Model Accuracy
                        </div>
            <div className="ori-l-mrgn-15">
              <Checkbox.Group
                options={source_filters}
                value={selected_source_filters}
                onChange={handleSourceChange}
              />
            </div>
          </div>
          <div className="ori-b-mrgn-10 ori-flex-row ori-flex-wrap">
            <RangePicker
              allowClear
              size="small"
              className="ori-range-picker-default"
              format="DD-MM-YYYY"
              placeholder={['Start Date', 'End Date']}
              value={selected_date_range}
              onChange={handleDateRangeChange}
            />
            <div className="ori-lr-pad-20">
              <Button
                type='primary'
                size='small'
                className='ori-font-xs ori-border-radius-20 ori-lr-pad-15'
                disabled={selected_source_filters.length === 0}
                onClick={handleGetIntentsData}
              >
                Get Data
              </Button>
            </div>
          </div>
        </div>
        {
          (count_data && (count_data.ReviewTotalCount > 0 || count_data.UtteranceTotalCount > 0 || count_data.ShortNameTotalCount > 0)) &&
          <div className="ori-tb-pad-10 ori-flex-row ori-flex-jsb">
            <div className="ori-flex-column">
              <div>{count_data.ReviewPercent >= 0 ? `Correct Review % : ${count_data.ReviewPercent}` : ""}</div>
              <div>{count_data.UtterancePercent >= 0 ? `Correct Utterance % : ${count_data.UtterancePercent}` : ""}</div>
              <div>{count_data.ShortNamePercent >= 0 ? `Correct ShortName % : ${count_data.ShortNamePercent}` : ""}</div>
            </div>
            <div className="ori-flex-column">
              <div>{count_data.ReviewCount >= 0 ? `Incorrect Review Count : ${count_data.ReviewCount}` : ""}</div>
              <div>{count_data.UtteranceCount >= 0 ? `Incorrect Utterance Count : ${count_data.UtteranceCount}` : ""}</div>
              <div>{count_data.ShortNameCount >= 0 ? `Incorrect ShortName Count  : ${count_data.ShortNameCount}` : ""}</div>
            </div>
            <div className="ori-flex-column">
              <div>{count_data.ReviewTotalCount >= 0 ? `Total Review Count : ${count_data.ReviewTotalCount}` : ""}</div>
              <div>{count_data.UtteranceTotalCount >= 0 ? `Total Utterance Count : ${count_data.UtteranceTotalCount}` : ""}</div>
              <div>{count_data.ShortNameTotalCount >= 0 ? `Total ShortName Count  : ${count_data.ShortNameTotalCount}` : ""}</div>
            </div>
          </div>
        }
        {
          correct_intents.length > 0 ?
            <Collapse
              className="ori-collapse-card ori-collapse-card-white ori-collapse-card-shadow-light"
              defaultActiveKey={['0']}
            >
              {
                correct_intents.map((intent_data, index) => (
                  <Panel header={`${intent_data.name} (${intent_data.count})`} key={index}>
                    <Table
                      className="ori-table-default ori-table-content-align-center ori-table-header-bg-transparent"
                      rowKey={uniqueId}
                      scroll={{ x: 600 }}
                      size={screen_width <= 576 ? "small" : "middle"}
                      columns={table_columns}
                      dataSource={intent_data.data}
                    />
                  </Panel>
                ))
              }
            </Collapse>
            : (
              table_columns.length === 0 ?
                <ShowMessage
                  img_src={IMG_EMPTY_MSG}
                  height={150}
                  width={150}
                  title='No filters selected'
                  message="Please select at least one filter and click on get data"
                /> :
                <ShowMessage
                  img_src={IMG_EMPTY}
                  height={150}
                  width={150}
                  title="No data found"
                  message="No data is available for the selected filters. Please try different filters."
                />
            )
        }
      </div>
    )
  else
    return (
      <ShowMessage
        img_src={IMG_ERROR}
        title="Error"
        message="unable to load data. please check your internet connection and reload the page."
        height={150}
        width={150}
      />
    )
}

const mapStateToProps = state => ({
  screen_width: state.page_details.device_data.screen_width,
  source_filters: state.model_accuracy_details.source_filters,
  selected_source_filters: state.model_accuracy_details.selected_source_filters,
  selected_date_range: state.model_accuracy_details.selected_date_range,
  correct_intents: state.model_accuracy_details.correct_intents,
  table_columns: state.model_accuracy_details.table_columns,
  intents_load_error: state.model_accuracy_details.intents_load_error,
  count_data: state.model_accuracy_details.count_data
})

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      pageChanged,
      getAccuracyFilters,
      getIntentsTableData,
      updateModelState
    },
    dispatch
  )
})

ModelAccuracy.propTypes = {
  actions: PropTypes.object,
  screen_width: PropTypes.number,
  source_filters: PropTypes.array,
  selected_source_filters: PropTypes.array,
  selected_date_range: PropTypes.array,
  correct_intents: PropTypes.array,
  table_columns: PropTypes.array,
  intents_load_error: PropTypes.bool,
  count_data: PropTypes.object
}

export default connect(mapStateToProps, mapDispatchToProps)(ModelAccuracy)



// WEBPACK FOOTER //
// ./src/app/pagecontainer/modules/analytics/modelaccuracy/index.js